import { cn } from "@/lib/utils";
import Image from "next/image";
import circlePlay from "../../assets/circle_play.svg";
import Video from "../atoms/Video";

interface StoriesCardProps {
  index: number;
  isActive: boolean;
  onPlay: () => void;
  onClose: () => void;
  data?: {
    image: string;
    alt_image?: string;
    name_text: string;
    designation: string;
    video_link: string;
    name_text_color: string;
    designation_text_color: string;
    gradient_start_color: string;
    gradient_end_color: string;
    gradient_angle?: string;
    gradient_start_position?: string;
    gradient_end_position?: string;
  };
}

const StoriesCard = ({
  index,
  isActive,
  onPlay,
  onClose,
  data,
}: StoriesCardProps) => {
  // Build gradient string with all customizable properties
  const getGradientStyle = () => {
    if (!data) return "";
    const angle = data.gradient_angle || "180deg";
    const startColor = data.gradient_start_color || "rgba(0, 0, 0, 0)";
    const endColor = data.gradient_end_color || "rgba(0, 0, 0, 0.7)";
    const startPosition = data.gradient_start_position || "0%";
    const endPosition = data.gradient_end_position || "100%";
    return `linear-gradient(${angle}, ${startColor} ${startPosition}, ${endColor} ${endPosition})`;
  };

  const hasVideo = data?.video_link && data.video_link !== "";

  return (
    <div
      className={cn(
        "relative rounded-xl overflow-hidden cursor-pointer",
        "xs:w-[196px] xs:h-[228px]",
        "sm:w-[238px] sm:h-[292px]",
        "md:w-[248px] md:h-[308px]",
        "lg:w-[300px] lg:h-[372px]",
        "xl:w-[360px] xl:h-[404px]"
      )}
    >
      {isActive && hasVideo ? (
        <Video src={data?.video_link || ""} onEnded={onClose} />
      ) : (
        data?.image && (
          <div
            className={cn(
              "relative rounded-xl overflow-hidden cursor-pointer",
              "xs:w-full xs:h-[228px]",
              "sm:w-[238px] sm:h-[292px]",
              "md:w-[248px] md:h-[308px]",
              "lg:w-[300px] lg:h-[372px]",
              "xl:w-[360px] xl:h-[404px]"
            )}
            onClick={hasVideo ? onPlay : undefined}
          >
            <Image
              src={data?.image || ""}
              alt={data?.alt_image || `Story ${index}`}
              width={200}
              height={300}
              className={cn("object-cover size-full")}
            />

            {/* Play Button - shown only when video exists */}
            {hasVideo && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Image
                  src={circlePlay || ""}
                  alt="Play video"
                  className={cn(
                    "transition-transform hover:scale-110",
                    "xs:w-10 xs:h-10",
                    "sm:w-12 sm:h-12",
                    "md:w-14 md:h-14",
                    "lg:w-16 lg:h-16",
                    "xl:w-[72px] xl:h-[72px]"
                  )}
                />
              </div>
            )}

            {/* Gradient overlay with name and designation */}
            <div
              className={cn(
                "absolute bottom-0 left-0 w-full text-white flex flex-col items-start justify-center",
                "xs:px-4 xs:py-2",
                "sm:px-4 sm:py-2"
              )}
              style={{
                background: getGradientStyle(),
              }}
            >
              {data?.name_text && (
                <div
                  className={cn(
                    "text-heading-8-bold",
                    "xs:line-clamp-2 xs:text-heading-7-bold",
                    "sm:text-heading-9-bold",
                    "md:text-heading-8-semibold",
                    "lg:text-heading-7-semibold",
                    "xl:text-heading-6-semibold"
                  )}
                  style={{ color: data?.name_text_color }}
                >
                  {data?.name_text}
                </div>
              )}
              {data?.designation && (
                <div
                  className={cn(
                    "xs:line-clamp-1 xs:text-heading-9-regular",
                    "sm:text-heading-9-regular sm:line-clamp-2",
                    "md:text-heading-9-regular",
                    "lg:text-heading-8-regular",
                    "xl:text-heading-7-regular"
                  )}
                  style={{ color: data?.designation_text_color }}
                >
                  {data?.designation}
                </div>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default StoriesCard;
